import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUserPreferences } from '../context/UserPreferencesContext';
import { getUserProfile, updateUserProfile, fetchAccounts } from '../api/api';
import { getMySubscription } from '../api/subscriptions';
import { getAIBalance } from '../api/billing';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import PageBackButton from '../components/PageBackButton';
import TelegramSettings from '../components/profile/TelegramSettings';
import { Shield, Zap, Crown, ArrowRight } from 'lucide-react';

// Minimal Select component using native select to stay consistent
function Select({ value, onChange, children, className }) {
  return (
    <select value={value ?? ''} onChange={(e) => onChange?.(e.target.value)} className={className}>
      {children}
    </select>
  );
}

const allowedCurrencies = [
  'USD','EUR','GBP','JPY','CHF','CAD','AUD','NZD','CNY','HKD','SGD','SEK','NOK','DKK','ZAR','MXN','BRL','INR'
];
const experienceOptions = ['BEGINNER','INTERMEDIATE','ADVANCED','PROFESSIONAL'];
const timezoneOptions = [
  'UTC','America/Los_Angeles','America/New_York','America/Chicago','America/Toronto','America/Sao_Paulo',
  'Europe/London','Europe/Berlin','Europe/Paris','Europe/Madrid','Europe/Rome','Europe/Amsterdam','Europe/Warsaw','Europe/Zurich','Europe/Vienna','Europe/Prague',
  'Africa/Johannesburg',
  'Asia/Dubai','Asia/Kolkata','Asia/Singapore','Asia/Hong_Kong','Asia/Shanghai','Asia/Tokyo',
  'Australia/Sydney','Pacific/Auckland'
];

export default function UserProfilePage() {
  const navigate = useNavigate();
  const { setTimezone, setLocale, setCurrency, setDefaultAccountId } = useUserPreferences() || {};
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [isApproved, setIsApproved] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const [aiBalance, setAiBalance] = useState(null);
  const originalRef = useRef(null);

  const [form, setForm] = useState({
    name: '',
    surname: '',
    email: '',
  phone: '',
  country: '',
  state: '',
  address: '',
    locale: '',
    city: '',
    postal_code: '',
    date_of_birth: '',
  notification_prefs: { email: true, sms: false, push: true },
  default_account: '',
  profile_currency: '',
  timezone: 'UTC',
  trading_experience: 'BEGINNER',
  });

  const setField = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));

  useEffect(() => {
    let alive = true;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const [profile, accts] = await Promise.all([
          getUserProfile(),
          fetchAccounts().catch(() => []),
        ]);
        // Fetch subscription and AI balance in background (non-blocking)
        Promise.all([
          getMySubscription().catch(() => null),
          getAIBalance().catch(() => null),
        ]).then(([subData, balData]) => {
          if (!alive) return;
          setSubscription(subData?.status !== 'none' ? subData : null);
          setAiBalance(balData);
        });
        if (!alive) return;
        const accs = Array.isArray(accts) ? accts : accts?.data || [];
        setAccounts(accs);
        // Normalize defaults
        const normalized = {
          name: profile?.name ?? '',
          surname: profile?.surname ?? '',
          email: profile?.email ?? '',
          phone: profile?.phone ?? '',
          country: profile?.country ?? '',
          state: profile?.state ?? '',
          address: profile?.address ?? '',
          locale: profile?.locale ?? '',
          city: profile?.city ?? '',
          postal_code: profile?.postal_code ?? '',
          date_of_birth: profile?.date_of_birth ?? '',
          notification_prefs: {
            email: profile?.notification_prefs?.email ?? true,
            sms: profile?.notification_prefs?.sms ?? false,
            push: profile?.notification_prefs?.push ?? true,
          },
          default_account: profile?.default_account ?? '',
          profile_currency: (profile?.profile_currency ?? '').toString().toUpperCase(),
          timezone: profile?.timezone || 'UTC',
          trading_experience: profile?.trading_experience || 'BEGINNER',
        };
        setIsApproved(Boolean(profile?.is_approved));
        setForm(normalized);
        originalRef.current = normalized;
      } catch (e) {
        if (!alive) return;
        setError(e?.message || 'Failed to load profile');
      } finally {
        if (alive) setLoading(false);
      }
    };
    load();
    return () => { alive = false; };
  }, []);

  // Countries (subset, alphabetized). Value uses country name for readability.
  const countries = useMemo(() => [
    { code: 'AF', name: 'Afghanistan' },
    { code: 'AL', name: 'Albania' },
    { code: 'DZ', name: 'Algeria' },
    { code: 'AR', name: 'Argentina' },
    { code: 'AM', name: 'Armenia' },
    { code: 'AU', name: 'Australia' },
    { code: 'AT', name: 'Austria' },
    { code: 'AZ', name: 'Azerbaijan' },
    { code: 'BH', name: 'Bahrain' },
    { code: 'BD', name: 'Bangladesh' },
    { code: 'BY', name: 'Belarus' },
    { code: 'BE', name: 'Belgium' },
    { code: 'BZ', name: 'Belize' },
    { code: 'BO', name: 'Bolivia' },
    { code: 'BA', name: 'Bosnia and Herzegovina' },
    { code: 'BR', name: 'Brazil' },
    { code: 'BG', name: 'Bulgaria' },
    { code: 'KH', name: 'Cambodia' },
    { code: 'CA', name: 'Canada' },
    { code: 'CL', name: 'Chile' },
    { code: 'CN', name: 'China' },
    { code: 'CO', name: 'Colombia' },
    { code: 'CR', name: 'Costa Rica' },
    { code: 'HR', name: 'Croatia' },
    { code: 'CY', name: 'Cyprus' },
    { code: 'CZ', name: 'Czechia' },
    { code: 'DK', name: 'Denmark' },
    { code: 'DO', name: 'Dominican Republic' },
    { code: 'EC', name: 'Ecuador' },
    { code: 'EG', name: 'Egypt' },
    { code: 'SV', name: 'El Salvador' },
    { code: 'EE', name: 'Estonia' },
    { code: 'ET', name: 'Ethiopia' },
    { code: 'FI', name: 'Finland' },
    { code: 'FR', name: 'France' },
    { code: 'GE', name: 'Georgia' },
    { code: 'DE', name: 'Germany' },
    { code: 'GH', name: 'Ghana' },
    { code: 'GR', name: 'Greece' },
    { code: 'GT', name: 'Guatemala' },
    { code: 'HN', name: 'Honduras' },
    { code: 'HK', name: 'Hong Kong' },
    { code: 'HU', name: 'Hungary' },
    { code: 'IS', name: 'Iceland' },
    { code: 'IN', name: 'India' },
    { code: 'ID', name: 'Indonesia' },
    { code: 'IE', name: 'Ireland' },
    { code: 'IL', name: 'Israel' },
    { code: 'IT', name: 'Italy' },
    { code: 'JP', name: 'Japan' },
    { code: 'JO', name: 'Jordan' },
    { code: 'KE', name: 'Kenya' },
    { code: 'KW', name: 'Kuwait' },
    { code: 'LV', name: 'Latvia' },
    { code: 'LB', name: 'Lebanon' },
    { code: 'LT', name: 'Lithuania' },
    { code: 'LU', name: 'Luxembourg' },
    { code: 'MY', name: 'Malaysia' },
    { code: 'MT', name: 'Malta' },
    { code: 'MX', name: 'Mexico' },
    { code: 'MD', name: 'Moldova' },
    { code: 'ME', name: 'Montenegro' },
    { code: 'MA', name: 'Morocco' },
    { code: 'NP', name: 'Nepal' },
    { code: 'NL', name: 'Netherlands' },
    { code: 'NZ', name: 'New Zealand' },
    { code: 'NG', name: 'Nigeria' },
    { code: 'MK', name: 'North Macedonia' },
    { code: 'NO', name: 'Norway' },
    { code: 'PK', name: 'Pakistan' },
    { code: 'PA', name: 'Panama' },
    { code: 'PY', name: 'Paraguay' },
    { code: 'PE', name: 'Peru' },
    { code: 'PH', name: 'Philippines' },
    { code: 'PL', name: 'Poland' },
    { code: 'PT', name: 'Portugal' },
    { code: 'QA', name: 'Qatar' },
    { code: 'RO', name: 'Romania' },
    { code: 'RU', name: 'Russia' },
    { code: 'SA', name: 'Saudi Arabia' },
    { code: 'RS', name: 'Serbia' },
    { code: 'SG', name: 'Singapore' },
    { code: 'SK', name: 'Slovakia' },
    { code: 'SI', name: 'Slovenia' },
    { code: 'ZA', name: 'South Africa' },
    { code: 'KR', name: 'South Korea' },
    { code: 'ES', name: 'Spain' },
    { code: 'LK', name: 'Sri Lanka' },
    { code: 'SE', name: 'Sweden' },
    { code: 'CH', name: 'Switzerland' },
    { code: 'TW', name: 'Taiwan' },
    { code: 'TH', name: 'Thailand' },
    { code: 'TN', name: 'Tunisia' },
    { code: 'TR', name: 'Turkey' },
    { code: 'UA', name: 'Ukraine' },
    { code: 'AE', name: 'United Arab Emirates' },
    { code: 'GB', name: 'United Kingdom' },
    { code: 'US', name: 'United States' },
    { code: 'UY', name: 'Uruguay' },
    { code: 'VE', name: 'Venezuela' },
    { code: 'VN', name: 'Vietnam' },
  ].sort((a, b) => a.name.localeCompare(b.name)), []);
  const countryOptions = useMemo(() => {
    const current = (form.country || '').toString();
    const names = new Set(countries.map(c => c.name));
    const needsFallback = current && !names.has(current);
    const list = [];
    if (needsFallback) list.push(<option key={`current-${current}`} value={current}>{current}</option>);
    countries.forEach(c => list.push(<option key={c.code} value={c.name}>{c.name}</option>));
    return list;
  }, [countries, form.country]);

  const deepEqualPrefs = (a, b) => (
    Boolean(a?.email) === Boolean(b?.email) &&
    Boolean(a?.sms) === Boolean(b?.sms) &&
    Boolean(a?.push) === Boolean(b?.push)
  );

  const computeDiff = () => {
    const orig = originalRef.current || {};
    const diff = {};
    const norm = (v) => (v === '' ? null : v);
    const keys = [
      'name','surname','email','phone','country','state','address','locale','city','postal_code','date_of_birth','default_account','profile_currency','timezone','trading_experience'
    ];
    keys.forEach((k) => {
      let val = form[k];
      if (k === 'profile_currency' && typeof val === 'string') val = val.toUpperCase();
      if (k === 'date_of_birth') val = norm(val);
      if (k === 'default_account') val = form.default_account === '' ? null : form.default_account;
      if (orig[k] !== val) diff[k] = val;
    });
    if (!deepEqualPrefs(form.notification_prefs, orig.notification_prefs)) {
      diff.notification_prefs = {
        email: !!form.notification_prefs?.email,
        sms: !!form.notification_prefs?.sms,
        push: !!form.notification_prefs?.push,
      };
    }
    // If timezone left blank, default to UTC per spec
    if (diff.timezone === '' || diff.timezone == null) {
      delete diff.timezone;
      if (orig.timezone !== 'UTC') diff.timezone = 'UTC';
    }
    return diff;
  };

  const onSubmit = async (e) => {
    e?.preventDefault?.();
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      // Client-side constraints
      if (form.profile_currency && !allowedCurrencies.includes(form.profile_currency.toUpperCase())) {
        throw new Error(`profile_currency must be one of: ${allowedCurrencies.join(', ')}`);
      }
      if (form.timezone && !timezoneOptions.includes(form.timezone)) {
        throw new Error('timezone must be a valid IANA value (choose from the list)');
      }
      const payload = computeDiff();
      if (Object.keys(payload).length === 0) {
        setSuccess('No changes to save');
        return;
      }
      await updateUserProfile(payload, { method: 'patch' });
      setSuccess('Profile updated');
      // Refresh originals so subsequent edits diff correctly
      const nextOriginal = { ...originalRef.current, ...payload };
      // Normalize capitalization and nullables
      if (typeof nextOriginal.profile_currency === 'string') nextOriginal.profile_currency = nextOriginal.profile_currency.toUpperCase();
      if (nextOriginal.date_of_birth === '') nextOriginal.date_of_birth = null;
      originalRef.current = nextOriginal;
  // Update global prefs so rest of app reflects changes immediately
  if (payload.timezone && setTimezone) setTimezone(payload.timezone);
  if (payload.locale && setLocale) setLocale(payload.locale);
  if (payload.profile_currency && setCurrency) setCurrency(payload.profile_currency);
  if (payload.default_account !== undefined && setDefaultAccountId) setDefaultAccountId(payload.default_account ?? null);
    } catch (e) {
      setError(e?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const accountOptions = useMemo(() => {
    return accounts.map((a) => (
      <option key={a.id} value={String(a.id)}>{a.name}</option>
    ));
  }, [accounts]);

  const currencyOptions = useMemo(() => allowedCurrencies.map(c => <option key={c} value={c}>{c}</option>), []);
  const timezoneOpts = useMemo(() => timezoneOptions.map(tz => <option key={tz} value={tz}>{tz}</option>), []);
  const experienceOpts = useMemo(() => experienceOptions.map(ex => <option key={ex} value={ex}>{ex}</option>), []);

  return (
    <div className="max-w-3xl mx-auto px-3 sm:px-0">
      <div className="flex items-center gap-3 mb-3 sm:mb-4">
        <PageBackButton label="Back to Settings" to="/settings" preferHistory={false} hideLabelOnMobile />
        <h1 className="text-xl sm:text-2xl font-semibold">User Profile</h1>
      </div>

      {/* ─── Subscription & Billing Summary ──────────────────────── */}
      {!loading && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
          {/* Subscription card */}
          <Card className="bg-transparent border border-white/10">
            <CardContent className="p-4">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Shield className="w-5 h-5 text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-white/50 uppercase tracking-wider">Subscription</p>
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-semibold text-white truncate">
                      {subscription
                        ? (subscription.tier_name || subscription.tier?.charAt(0).toUpperCase() + subscription.tier?.slice(1))
                        : 'No Plan'}
                    </p>
                    {subscription && (
                      <Badge variant={subscription.status === 'active' ? 'success' : 'warning'}>
                        {subscription.status}
                      </Badge>
                    )}
                  </div>
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                className="w-full mt-1 text-xs"
                onClick={() => window.location.href = '/subscription'}
              >
                <Crown className="w-3.5 h-3.5 mr-1" />
                Manage Plan
                <ArrowRight className="w-3.5 h-3.5 ml-auto" />
              </Button>
            </CardContent>
          </Card>

          {/* Credits card */}
          <Card className="bg-transparent border border-white/10">
            <CardContent className="p-4">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-9 h-9 rounded-lg bg-success/10 flex items-center justify-center">
                  <Zap className="w-5 h-5 text-success" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-white/50 uppercase tracking-wider">Credits</p>
                  <p className="text-sm font-semibold text-white">
                    {aiBalance
                      ? Number(aiBalance.balance).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                      : '—'}
                  </p>
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                className="w-full mt-1 text-xs"
                onClick={() => window.location.href = '/billing?tab=credits'}
              >
                <Zap className="w-3.5 h-3.5 mr-1" />
                Billing & Credits
                <ArrowRight className="w-3.5 h-3.5 ml-auto" />
              </Button>
            </CardContent>
          </Card>
        </div>
      )}

      <Card className="bg-transparent border border-white/10">
        <CardContent className="p-3 sm:p-4">
          {loading ? (
            <div className="text-sm text-white/70">Loading…</div>
          ) : (
            <form onSubmit={onSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
              {error && <div className="md:col-span-2 text-xs sm:text-sm text-rose-400">{error}</div>}
              {success && <div className="md:col-span-2 text-xs sm:text-sm text-emerald-400">{success}</div>}

              <div>
                <label className="block text-xs text-white/70 mb-1">Name</label>
                <Input value={form.name} onChange={(e) => setField('name', e.target.value)} className="bg-[rgb(28,33,37)] border-white/10 text-white" />
              </div>
              <div>
                <label className="block text-xs text-white/70 mb-1">Surname</label>
                <Input value={form.surname} onChange={(e) => setField('surname', e.target.value)} className="bg-[rgb(28,33,37)] border-white/10 text-white" />
              </div>
              <div>
                <label className="block text-xs text-white/70 mb-1">Email</label>
                <Input type="email" value={form.email} onChange={(e) => setField('email', e.target.value)} className="bg-[rgb(28,33,37)] border-white/10 text-white" />
              </div>
              <div>
                <label className="block text-xs text-white/70 mb-1">Phone</label>
                <Input value={form.phone} onChange={(e) => setField('phone', e.target.value)} className="bg-[rgb(28,33,37)] border-white/10 text-white" />
              </div>
              {/* Address block reordered: Address → City → State → Postal Code → Country */}
              <div className="md:col-span-2">
                <label className="block text-xs text-white/70 mb-1">Address</label>
                <Input value={form.address} onChange={(e) => setField('address', e.target.value)} className="bg-[rgb(28,33,37)] border-white/10 text-white" />
              </div>
              <div>
                <label className="block text-xs text-white/70 mb-1">City</label>
                <Input value={form.city} onChange={(e) => setField('city', e.target.value)} className="bg-[rgb(28,33,37)] border-white/10 text-white" />
              </div>
              <div>
                <label className="block text-xs text-white/70 mb-1">State/Region</label>
                <Input value={form.state} onChange={(e) => setField('state', e.target.value)} className="bg-[rgb(28,33,37)] border-white/10 text-white" />
              </div>
              <div>
                <label className="block text-xs text-white/70 mb-1">Postal Code</label>
                <Input value={form.postal_code} onChange={(e) => setField('postal_code', e.target.value)} className="bg-[rgb(28,33,37)] border-white/10 text-white" />
              </div>
              <div>
                <label className="block text-xs text-white/70 mb-1">Country</label>
                <Select value={form.country} onChange={(v) => setField('country', v)} className="w-full bg-[rgb(28,33,37)] border border-white/10 text-white rounded-md h-9 px-2">
                  <option value="">Select country</option>
                  {countryOptions}
                </Select>
              </div>
              {/* End address block */}
              <div>
                <label className="block text-xs text-white/70 mb-1">Locale</label>
                <Input value={form.locale} onChange={(e) => setField('locale', e.target.value)} placeholder="en-US" className="bg-[rgb(28,33,37)] border-white/10 text-white" />
              </div>
              <div>
                <label className="block text-xs text-white/70 mb-1">Date of Birth</label>
                <Input type="date" value={form.date_of_birth || ''} onChange={(e) => setField('date_of_birth', e.target.value)} className="bg-[rgb(28,33,37)] border-white/10 text-white" />
              </div>
              <div>
                <label className="block text-xs text-white/70 mb-1">Timezone</label>
                <Select value={form.timezone} onChange={(v) => setField('timezone', v)} className="w-full bg-[rgb(28,33,37)] border border-white/10 text-white rounded-md h-9 px-2">
                  {timezoneOpts}
                </Select>
              </div>
              <div>
                <label className="block text-xs text-white/70 mb-1">Profile Currency</label>
                <Select value={form.profile_currency} onChange={(v) => setField('profile_currency', v.toUpperCase())} className="w-full bg-[rgb(28,33,37)] border border-white/10 text-white rounded-md h-9 px-2">
                  <option value="">Select currency</option>
                  {currencyOptions}
                </Select>
              </div>
              <div>
                <label className="block text-xs text-white/70 mb-1">Trading Experience</label>
                <Select value={form.trading_experience} onChange={(v) => setField('trading_experience', v)} className="w-full bg-[rgb(28,33,37)] border border-white/10 text-white rounded-md h-9 px-2">
                  {experienceOpts}
                </Select>
              </div>
              <div>
                <label className="block text-xs text-white/70 mb-1">Default Account</label>
                <Select
                  value={form.default_account === null ? '' : (form.default_account === '' ? '' : String(form.default_account))}
                  onChange={(v) => {
                    if (!v) return setField('default_account', null);
                    const found = accounts.find(a => String(a.id) === v);
                    setField('default_account', found ? found.id : v);
                  }}
                  className="w-full bg-[rgb(28,33,37)] border border-white/10 text-white rounded-md h-9 px-2">
                  <option value="">None</option>
                  {accountOptions}
                </Select>
              </div>
              <div className="md:col-span-2">
                <div className="text-sm font-medium text-white/90 mb-1">Notifications</div>
                <label className="inline-flex items-center gap-2 mr-4 text-xs text-white/80">
                  <input type="checkbox" className="accent-cyan-500" checked={!!form.notification_prefs?.email} onChange={(e) => setField('notification_prefs', { ...form.notification_prefs, email: e.target.checked })} />
                  Email
                </label>
                <label className="inline-flex items-center gap-2 text-xs text-white/80">
                  <input type="checkbox" className="accent-cyan-500" checked={!!form.notification_prefs?.sms} onChange={(e) => setField('notification_prefs', { ...form.notification_prefs, sms: e.target.checked })} />
                  SMS
                </label>
                <label className="inline-flex items-center gap-2 ml-4 text-xs text-white/80">
                  <input type="checkbox" className="accent-cyan-500" checked={!!form.notification_prefs?.push} onChange={(e) => setField('notification_prefs', { ...form.notification_prefs, push: e.target.checked })} />
                  Push
                </label>
                <div className="mt-3">
                  <TelegramSettings />
                </div>
              </div>

              <div className="md:col-span-2 text-xs text-white/60">
                <span className="font-medium">Approval status:</span> {isApproved ? <span className="text-emerald-400">Approved</span> : <span className="text-amber-300">Pending</span>}
              </div>

              <div className="md:col-span-2 flex justify-end gap-2 mt-2">
                <Button type="button" variant="outline" className="border-white/10" onClick={() => navigate('/settings')}>
                  Cancel
                </Button>
                <Button type="submit" disabled={saving}>{saving ? 'Saving…' : 'Save Changes'}</Button>
              </div>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
